import './styles.scss';

import { Button, Card } from 'react-bootstrap';
import { DtrParsedTrip, PotentialLocation } from '@/models/gen/graphql';

import FormField from '@/components/FormField';
import Tippy from '@tippyjs/react';
import TippyWhen from '@/components/TippyWhen';
import { formatRoutekey } from '../../utils/locations';
import { useDriveTimeToolRouteSelection } from '../../pages/RouteSelection';

const LocationCard = ({
  puLocation,
  doLocation,
  trips,
  selected,
  airportCode,
  onResolveLocations,
}: {
  puLocation: PotentialLocation;
  doLocation: PotentialLocation;
  trips?: DtrParsedTrip[];
  selected: boolean;
  airportCode: string;
  onResolveLocations: () => void;
}) => {
  const setState = useDriveTimeToolRouteSelection(({ setState }) => setState);
  const handleSelectRoute = () => {
    const routeKey = formatRoutekey(puLocation.name, airportCode, doLocation.name, airportCode);
    if (!puLocation?.dtr_location_id || !doLocation?.dtr_location_id) return;
    setState((current) => ({
      ...current,
      selected: current.selected.includes(routeKey) ? current.selected.filter((key) => key !== routeKey) : [...current.selected, routeKey],
    }));
  };

  return (
    <Card className="location-card">
      <Card.Body>
        <div className="location-header">
          <div className="header-content d-flex flex-nowrap w-100">
            <div className="header-left flex-grow-1 d-flex">
              <TippyWhen
                isTrue={!puLocation?.dtr_location_id || !doLocation?.dtr_location_id}
                options={{ content: 'Resolve missing location to import.' }}
              >
                <span>
                  <FormField
                    type="checkbox"
                    name={`location-${formatRoutekey(puLocation?.name, airportCode, doLocation?.name, airportCode)}`}
                    checked={selected}
                    onChange={handleSelectRoute}
                    disabled={!puLocation?.dtr_location_id || !doLocation?.dtr_location_id}
                    options={{ group: { className: 'me-3 flex-grow-0 w-auto' } }}
                  />
                </span>
              </TippyWhen>
              <div className="header-text d-flex flex-column gap-3 flex-grow-1">
                <div className="fw-semibold d-flex align-items-center gap-2 flex-nowrap">
                  <Tippy content={FormatLocationAddress(puLocation, airportCode) ?? 'Resolve missing location to import.'}>
                    <span className={`${!puLocation?.dtr_location_id ? 'text-danger' : ''} d-flex align-items-center gap-2`}>
                      <i className="sv sv-map-marker fs-4" />
                      {puLocation?.name}
                    </span>
                  </Tippy>
                  <i className="sv sv-arrow-right mx-2" />
                  <Tippy content={FormatLocationAddress(doLocation, airportCode) ?? 'Resolve missing location to import.'}>
                    <span className={`${!doLocation?.dtr_location_id ? 'text-danger' : ''} d-flex align-items-center gap-2`}>
                      <i className="sv sv-map-marker fs-4" />
                      {doLocation?.name}
                    </span>
                  </Tippy>
                </div>
                {(!puLocation?.dtr_location_id || !doLocation?.dtr_location_id) && (
                  <div className="fw-semibold">
                    <span className="px-2 me-2 rounded-circle bg-red-emoji" />
                    Unknown Location - {trips?.length} trips affected
                    <Button variant="green" className="ms-3 btn-shadow" onClick={onResolveLocations}>
                      Resolve
                    </Button>
                  </div>
                )}
                {!!puLocation?.dtr_location_id && !!doLocation?.dtr_location_id && (
                  <div className="fw-semibold">
                    <span className="px-2 me-2 rounded-circle bg-green-emoji" />
                    Mapped Location (OK)
                  </div>
                )}
                <div>
                  <i className="sv sv-repeat" /> {trips?.length} Trips
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const FormatLocationAddress = (loc: PotentialLocation, airportCode: string): React.JSX.Element | null => {
  if (!loc?.dtr_location_id) return null;

  const address = [loc.address, loc.cityName, loc.stateCode, loc.zipCode, loc.countryCode].filter(Boolean).join(', ');

  if (!address) return null;
  return (
    <div className="d-flex flex-column gap-3">
      <h5 className="m-0">{airportCode}</h5>
      <p className="m-0">{address}</p>
    </div>
  );
};

export default LocationCard;
